// src/components/exam-player/renderers/DragDropItemTargetRenderer.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export default function DragDropItemTargetRenderer({
  item,
  onAnswerChange,
  initialAnswers,
  questionNumber,
  hideStem,
}) {
  const { i18n, t } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  const body = item?.body || {};
  const targets = Array.isArray(body.targets) ? body.targets : [];
  const items = Array.isArray(body.items) ? body.items : [];
  const boardImageUrl = body.board?.imageUrl || "";
  const layout = body.layout || "top-bottom";

  // ---- مقدار اولیه از initialAnswers (فقط یک بار برای هر آیتم) ----
  const computeInitialAssignments = () => {
    const res = {};
    if (initialAnswers && typeof initialAnswers === "object") {
      const src =
        initialAnswers.assignments &&
        typeof initialAnswers.assignments === "object"
          ? initialAnswers.assignments
          : initialAnswers;

      items.forEach((it, idx) => {
        const key = String(it.key || `I${idx + 1}`);
        const tk = src[key];
        if (tk != null && tk !== "") res[key] = String(tk);
      });
    }
    return res;
  };

  const [assignments, setAssignments] = useState(computeInitialAssignments);
  const [dragItemKey, setDragItemKey] = useState(null);
  const [pendingItemKey, setPendingItemKey] = useState(null);

  // وقتی خود آیتم (سؤال) عوض شد، state داخلی رو ریست کن
  useEffect(() => {
    setAssignments(computeInitialAssignments());
    setDragItemKey(null);
    setPendingItemKey(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?._id]);

  // هر بار assignments عوض شد، به بالا پاس بده
  useEffect(() => {
    onAnswerChange?.(assignments);
  }, [assignments, onAnswerChange]);

  // ---- map: targetKey → [itemKey, itemKey, ...] (چند آیتم روی یک هدف) ----
  const itemsByTargetKey = useMemo(() => {
    const map = {};
    Object.entries(assignments).forEach(([itemKey, targetKey]) => {
      if (!targetKey) return;
      const tk = String(targetKey);
      if (!map[tk]) map[tk] = [];
      map[tk].push(String(itemKey));
    });
    return map;
  }, [assignments]);

  // آیتم‌های بدون target
  const unassignedItems = useMemo(
    () =>
      items.filter((it, idx) => {
        const key = String(it.key || `I${idx + 1}`);
        return !assignments[key];
      }),
    [items, assignments]
  );

  const getItemLabel = (it) =>
    (it?.label?.fa ??
      it?.fa ??
      it?.label?.en ??
      it?.en ??
      it?.key ??
      "") || "";

  const getTargetLabel = (tg) =>
    (tg?.label?.fa ??
      tg?.fa ??
      tg?.label?.en ??
      tg?.en ??
      tg?.key ??
      "") || "";

  // ---- اینجا دیگه قبلی‌های آن target رو پاک نمی‌کنیم → multi-assign مجاز ----
  const assignItemToTarget = (itemKey, targetKey) => {
    if (!itemKey || !targetKey) return;

    setAssignments((prev) => ({
      ...prev,
      [String(itemKey)]: String(targetKey),
    }));

    setPendingItemKey(null);
    setDragItemKey(null);
  };

  const handleItemDragStart = (itemKey) => (e) => {
    setDragItemKey(itemKey);
    try {
      e.dataTransfer?.setData("text/plain", itemKey);
    } catch (_) {}
  };

  const handleTargetDrop = (targetKey) => (e) => {
    e.preventDefault();
    let itemKey = dragItemKey;
    if (!itemKey && e.dataTransfer) {
      try {
        const dt = e.dataTransfer.getData("text/plain");
        if (dt) itemKey = dt;
      } catch (_) {}
    }
    if (!itemKey) return;
    assignItemToTarget(itemKey, targetKey);
  };

  const handleTargetDragOver = (e) => {
    e.preventDefault();
  };

  const handleItemClick = (itemKey) => {
    setPendingItemKey((prev) => (prev === itemKey ? null : itemKey));
  };

  const handleTargetClick = (targetKey) => {
    if (pendingItemKey) {
      assignItemToTarget(pendingItemKey, targetKey);
    }
  };

  const handleUnassignItem = (itemKey) => {
    setAssignments((prev) => {
      const next = { ...prev };
      delete next[itemKey];
      return next;
    });
  };

  const stemHtml = item?.stem?.fa || item?.stem?.en || "";
  const boardFirst =
    layout === "top-bottom" || layout === "left-right" ? true : false;

  // ---------- برد (board) با چند آیتم روی هر target ----------
  const boardBlock = (
    <div className="space-y-3 flex justify-center">
      {boardImageUrl ? (
        <div className="relative inline-block  border rounded-lg overflow-hidden max-w-[970px]">
          <img
            src={boardImageUrl}
            alt="Board"
            className="h-full mx-auto"
          />
          <div className="absolute inset-0 ">
            {targets.map((target, idx) => {
              const rect = target.rect;
              if (!rect || !rect.w || !rect.h) return null;

              const targetKey = String(target.key || `T${idx + 1}`);
              const assignedItemKeys = itemsByTargetKey[targetKey] || [];

              const assignedItems = items.filter((it) =>
                assignedItemKeys.includes(String(it.key))
              );

              return (
                <div
                  key={targetKey}
                  className="absolute border-2 border-indigo-500/70 bg-indigo-500/15 text-[10px] md:text-xs text-white flex items-center justify-center text-center cursor-pointer"
                  style={{
                    left: rect.x,
                    top: rect.y,
                    width: rect.w,
                    height: rect.h,
                  }}
                  onClick={() => handleTargetClick(targetKey)}
                  onDrop={handleTargetDrop(targetKey)}
                  onDragOver={handleTargetDragOver}
                >
                  <div className="px-1 space-y-1">
                    <div className="font-semibold">
                      {targetKey}
                      {getTargetLabel(target)
                        ? ` – ${getTargetLabel(target)}`
                        : ""}
                    </div>

                    {assignedItems.length > 0 ? (
                      <div className="flex flex-wrap gap-1 justify-center">
                        {assignedItems.map((it) => (
                          <div
                            key={`badge-${targetKey}-${it.key}`}
                            className="inline-flex items-center px-1.5 py-0.5 rounded bg-indigo-600/80"
                          >
                            {getItemLabel(it)}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="opacity-70">
                        {t("dragdrop_it.drop_here", "رها کن اینجا")}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-xs text-gray-500">
          {t("dragdrop_it.no_board", "تصویری تعریف نشده است")}
        </div>
      )}
    </div>
  );

  // ---------- بانک آیتم‌ها + خلاصه‌ی انتساب‌ها ----------
  const itemsBlock = (
    <div className="space-y-2">
      <div className="text-xs text-gray-600 mb-1">
        {t("dragdrop_it.items_bank", "آیتم‌ها را بکشید و رها کنید")}
      </div>
      <div className="flex flex-wrap gap-2">
        {unassignedItems.length === 0 && (
          <span className="text-xs text-gray-400">
            {t("dragdrop_it.no_unassigned", "آیتم آزاد باقی نمانده است")}
          </span>
        )}
        {unassignedItems.map((it, idx) => {
          const key = String(it.key || `I${idx + 1}`);
          const label = getItemLabel(it) || key;
          const isPending = pendingItemKey === key;

          return (
            <div
              key={key}
              draggable
              onDragStart={handleItemDragStart(key)}
              onClick={() => handleItemClick(key)}
              className={`px-3 py-1.5 rounded-full border text-xs md:text-sm cursor-move select-none
                ${
                  isPending
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-gray-800 border-gray-300 hover:border-indigo-400"
                }`}
            >
              {label}
            </div>
          );
        })}
      </div>

      {/* خلاصه‌ی آیتم‌ها و target فعلی‌شان */}
      {items.length > 0 && (
        <div className="mt-3 text-[11px] md:text-xs text-gray-600 space-y-1">
          {items.map((it, idx) => {
            const itemKey = String(it.key || `I${idx + 1}`);
            const assignedTargetKey = assignments[itemKey];
            const target =
              assignedTargetKey &&
              targets.find(
                (tg) => String(tg.key) === String(assignedTargetKey)
              );
            const itemLabel = getItemLabel(it) || itemKey;
            const targetLabel =
              target &&
              (getTargetLabel(target) ||
                String(target.key || assignedTargetKey));

            return (
              <div
                key={`summary-${itemKey}`}
                className="flex items-center justify-between gap-2"
              >
                <span className="font-medium">{itemLabel}</span>
                <span className="flex-1 border-t border-dotted border-gray-300 mx-2" />
                {assignedTargetKey ? (
                  <span className="inline-flex items-center gap-1">
                    <span className="px-1.5 py-0.5 rounded bg-slate-100">
                      {targetLabel}
                    </span>
                    <button
                      type="button"
                      className="text-[10px] text-red-500 hover:underline"
                      onClick={() => handleUnassignItem(itemKey)}
                    >
                      {t("dragdrop_it.remove_assignment", "حذف")}
                    </button>
                  </span>
                ) : (
                  <span className="text-gray-400">
                    {t("dragdrop_it.not_assigned", "انتخاب نشده")}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div
      className={`space-y-3 ${
        isRtl ? "text-right" : "text-left"
      } dragdrop-it-renderer`}
    >
      {!hideStem && stemHtml && (
        <div
          className="prose prose-sm max-w-none mb-2"
          dir={isRtl ? "rtl" : "ltr"}
          dangerouslySetInnerHTML={{ __html: stemHtml }}
        />
      )}

      {layout === "left-right" || layout === "right-left" ? (
        <div
          className={`flex flex-col md:flex-row gap-4 ${
            layout === "right-left" ? "md:flex-row-reverse" : ""
          }`}
        >
          <div className="flex-1">{boardBlock}</div>
          <div className="flex-1">{itemsBlock}</div>
        </div>
      ) : boardFirst ? (
        <>
          {boardBlock}
          {itemsBlock}
        </>
      ) : (
        <>
          {itemsBlock}
          {boardBlock}
        </>
      )}
    </div>
  );
}
